/* Shared motion is opt-in. Campaign, menu, galleries and commerce own themselves. */
(() => {
  'use strict';
  if (window.DuooMotionStudio || document.documentElement.dataset.duooInteractions !== 'true') return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const active = new Map();
  const seen = new WeakSet();
  const observing = new Set();
  const pending = new Set();
  const targets = '[data-duoo-enter]';
  const excluded = '[data-duoo-motion-owner],[data-duoo-campaign],[data-duoo-sheet],dialog';
  const time = (name, fallback) => {
    const token = getComputedStyle(document.documentElement).getPropertyValue('--duoo-motion-' + name).trim();
    const match = token.match(/^(\d+(?:\.\d*)?|\.\d+)(ms|s)$/i);
    if (!match) return fallback;
    const milliseconds = Number(match[1]) * (match[2].toLowerCase() === 's' ? 1000 : 1);
    return Number.isFinite(milliseconds) ? milliseconds : fallback;
  };
  const cancel = scope => active.forEach((animation,node) => {
    if (!scope || scope === node || scope.contains(node)) { active.delete(node); animation.cancel(); }
  });
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    if (document.hidden) return;
    entries.filter(entry => entry.isIntersecting).forEach(entry => {
      const node = entry.target;
      observer.unobserve(node); observing.delete(node); seen.add(node);
      if (reduced.matches || !node.isConnected || node.closest(excluded)) return;
      const distance = innerWidth < 768 ? 8 : 16;
      const duration = time('content',420);
      const animation = node.animate([{translate:distance+'px 0'},{translate:'0 0'}], {duration,easing:'cubic-bezier(.22,1,.36,1)',fill:'both'});
      active.set(node,animation);
      const settle = () => { if(active.get(node) === animation) { active.delete(node); animation.cancel(); } };
      animation.finished.then(settle,settle);
    });
  },{threshold:.12,rootMargin:'0px 0px -18px 0px'}) : null;

  function mount(scope = document) {
    observing.forEach(node => { if(!node.isConnected) { observer?.unobserve(node); observing.delete(node); } });
    const nodes = [...scope.querySelectorAll(targets)];
    if(scope.matches?.(targets)) nodes.unshift(scope);
    nodes.filter(node => !seen.has(node) && !observing.has(node) && !node.closest(excluded) && !nodes.some(parent => parent !== node && parent.contains(node))).forEach(node => {
      const rect = node.getBoundingClientRect();
      if(!rect.width || !rect.height || node.closest('[hidden]')) return;
      // Already readable content is never hidden or restarted by a late script.
      if(reduced.matches || (rect.top < innerHeight && rect.bottom > 0)) seen.add(node);
      else if(observer) { observing.add(node); observer.observe(node); }
    });
  }
  function queueMount(scope = document) {
    if(pending.has(scope)) return;
    pending.add(scope);
    const settle = () => { pending.delete(scope); if(scope === document || scope.isConnected) mount(scope); };
    Promise.resolve(window.DuooRuntime?.afterTransition?.()).then(settle,settle);
  }
  document.addEventListener('duoo:fragment-mount',event => queueMount(event.detail?.root || document));
  document.addEventListener('duoo:fragment-unmount',event => {
    const scope = event.detail?.root;
    if(!scope) return;
    cancel(scope);
    observing.forEach(node => { if(scope === node || scope.contains(node)) { observer?.unobserve(node); observing.delete(node); } });
  });
  document.addEventListener('shopify:section:load',event => queueMount(event.target));
  document.addEventListener('duoo:page-change',() => queueMount());
  reduced.addEventListener('change',() => { if(reduced.matches) { cancel(); observing.forEach(node => { seen.add(node); observer?.unobserve(node); }); observing.clear(); } });
  window.addEventListener('pagehide',() => cancel());
  window.addEventListener('pageshow',() => queueMount());
  document.addEventListener('visibilitychange',() => { if(document.hidden) cancel(); else queueMount(); });
  window.DuooMotionStudio = Object.freeze({mount:queueMount,version:6});
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => mount(),{once:true});
  else mount();
})();
